'use memo'

import { useSelector } from 'src/hooks/useRedux'
import { getGenderTypes } from '@sportninja/common/selectors/types'
import { usePositionOptions } from 'src/features/team/player/hooks/useRosterPlayer'
import type { CompetitionResult } from '@sportninja/common/features/teamFinder/types'

interface ResultLabels {
  /** Division gender category label (e.g. "Men's"), or undefined. */
  genderLabel?: string
  /** Resolved position names the team needs (e.g. ["Goalie"]). */
  positionNames: string[]
}

interface GenderType {
  id: string | number
  name?: string
  name_full?: string
}

/**
 * Resolve a result's `genderTypeId` → gender label and `positionsNeeded` ids →
 * position names, using the same Redux type stores the roster/division screens
 * use. Position names resolve off the result's own `sportId`.
 */
export const useResultLabels = (result: CompetitionResult): ResultLabels => {
  const genderTypes = useSelector<GenderType[]>((state) =>
    getGenderTypes(state)
  )
  const positionOptions = usePositionOptions(result.sportId ?? null)

  const genderLabel =
    result.genderTypeId != null
      ? (() => {
          const match = genderTypes.find(
            (g) => String(g.id) === String(result.genderTypeId)
          )
          return match?.name_full ?? match?.name ?? undefined
        })()
      : undefined

  const positionById = new Map(positionOptions.map((o) => [o.value, o.label]))
  const positionNames = (result.positionsNeeded ?? [])
    .map((id) => positionById.get(String(id)))
    .filter((name): name is string => Boolean(name))

  return { genderLabel, positionNames }
}
